import { ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FadeInOnLoadProps {
    children: ReactNode;
    className?: string;
    /** Direction to slide in from */
    direction?: 'none' | 'right' | 'bottom';
}

/**
 * FadeInOnLoad - Simple fade-in wrapper for async content.
 * 
 * Use this to wrap content that appears after data loads.
 * Provides a subtle 200ms fade to prevent jarring content pops.
 * 
 * @example
 * ```tsx
 * {isLoading ? (
 *   <Skeleton className="h-40" />
 * ) : (
 *   <FadeInOnLoad>
 *     <div>{data.content}</div>
 *   </FadeInOnLoad>
 * )}
 * ```
 */
export function FadeInOnLoad({
    children,
    className,
    direction = 'none'
}: FadeInOnLoadProps) {
    const base = "animate-in fade-in duration-200 ease-out motion-reduce:animate-none motion-reduce:opacity-100";
    const directional =
        direction === 'right'
            ? "slide-in-from-right-4"
            : direction === 'bottom'
                ? "slide-in-from-bottom-4"
                : "";

    return (
        <div
            className={cn(
                base,
                directional,
                className
            )}
        >
            {children}
        </div>
    );
}
